import { useMemo } from 'react';
import { C } from '../../constants/colors.js';
import { F, N } from '../../constants/fonts.js';

const TEAL = C.teal || C.secondary;
const NAVY = C.navy || C.text;
const WARM_WHITE = C.warmWhite || C.bg;

const CONCERN_MODE_MAP = {
  'Struggles to sound out new words': {
    modes: ['phonics'],
    description: 'Phonics mode prioritized — building sound-letter connections',
  },
  'Reads slowly or word-by-word': {
    modes: ['phonics', 'stories'],
    description: 'Fluency practice through phonics drills and guided stories',
  },
  "Forgets words they've seen before": {
    modes: ['sight'],
    description: 'Smart spaced review activated — words repeat at helpful intervals',
  },
  "Doesn't enjoy reading": {
    modes: ['bubbles', 'stories'],
    description: 'Game-first approach — Bubble Pop and Stories make reading fun',
  },
  'Behind where they should be': {
    modes: ['phonics', 'sight'],
    description: 'Targeted support — Finley focuses on gaps and builds confidence quickly',
  },
  'Supplement school or homeschool': {
    modes: ['phonics', 'sight', 'stories'],
    description: 'Full coverage — phonics, sight words, and comprehension support',
  },
  'Loves reading — wants to go further': {
    modes: ['stories', 'sight'],
    description: 'Advanced content unlocked — challenging stories and vocabulary',
  },
  'Just exploring': {
    modes: ['phonics', 'sight', 'bubbles', 'stories'],
    description: 'Finley finds the right starting point automatically',
  },
};

const MODES = [
  { id: 'phonics', icon: '📖', label: 'Phonics' },
  { id: 'sight', icon: '👁', label: 'Sight Words' },
  { id: 'bubbles', icon: '🫧', label: 'Bubble Pop' },
  { id: 'stories', icon: '📚', label: 'Stories' },
];

export function PersonalizedPreview({ onboardingData, onContinue }) {
  const concernDetails = onboardingData.concerns
    .map((concern) => ({ concern, mapped: CONCERN_MODE_MAP[concern] }))
    .filter((entry) => entry.mapped);

  const activeModes = useMemo(() => {
    const s = new Set();
    concernDetails.forEach((entry) => entry.mapped.modes.forEach((mode) => s.add(mode)));
    if (s.size === 0) MODES.forEach((m) => s.add(m.id));
    return s;
  }, [concernDetails]);

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px' }}>
      <h2 style={{ fontFamily: F, color: NAVY, fontSize: 27, lineHeight: 1.2, marginBottom: 8 }}>
        Your child's personalized plan
      </h2>
      <p style={{ fontFamily: N, color: 'rgba(15,43,60,0.72)', fontSize: 15, lineHeight: 1.5, marginBottom: 16 }}>
        Based on what you told us, Finley will focus on:
      </p>

      <div
        style={{
          background: WARM_WHITE,
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 4px 12px rgba(15,43,60,0.08)',
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {MODES.map((mode) => {
            const highlighted = activeModes.has(mode.id);
            return (
              <div
                key={mode.id}
                style={{
                  borderRadius: 12,
                  border: `2px solid ${highlighted ? TEAL : '#E5EBF0'}`,
                  background: highlighted ? 'rgba(13,148,136,0.12)' : '#fff',
                  opacity: highlighted ? 1 : 0.45,
                  padding: '10px 12px',
                  fontFamily: N,
                  color: NAVY,
                  fontSize: 14,
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: 18 }}>{mode.icon}</span>
                <span>{mode.label}</span>
              </div>
            );
          })}
        </div>

        <div style={{ height: 1, background: '#E5EBF0', marginBottom: 12 }} />
        <div style={{ fontFamily: F, fontSize: 17, color: NAVY, marginBottom: 10 }}>How it works</div>

        <div style={{ display: 'grid', gap: 10 }}>
          {concernDetails.map(({ concern, mapped }) => (
            <div key={concern} style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5EBF0', padding: '10px 12px' }}>
              <div style={{ fontFamily: N, color: NAVY, fontSize: 14, marginBottom: 4 }}>{concern}</div>
              <div style={{ fontFamily: N, color: 'rgba(15,43,60,0.75)', fontSize: 13 }}>{mapped.description}</div>
            </div>
          ))}
        </div>

        <p style={{ margin: '14px 0 0', fontFamily: N, color: NAVY, fontSize: 14, lineHeight: 1.5 }}>
          Smart practice that adapts to your child's progress every session.
        </p>
      </div>

      <div
        style={{
          background: NAVY,
          borderRadius: 16,
          padding: 24,
          marginBottom: 16,
          boxShadow: '0 4px 12px rgba(15,43,60,0.2)',
          border: '1px solid rgba(255, 255, 255, 0.10)',
        }}
      >
        <div style={{ color: '#fff', fontSize: 16, marginBottom: 8 }}>⭐⭐⭐⭐⭐</div>
        <p style={{ margin: 0, color: '#fff', fontFamily: N, fontSize: 15, lineHeight: 1.5, fontStyle: 'italic' }}>
          "My son went from hating reading to asking for 'one more level' every night."
        </p>
        <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.78)', fontFamily: N, fontSize: 13 }}>— Sarah M., Homeschool Mom</div>
        <div style={{ marginTop: 12, color: 'rgba(255,255,255,0.9)', fontFamily: N, fontSize: 13 }}>
          50K+ sessions · 12K+ words mastered · 4.8 rating
        </div>
      </div>

      <button
        onClick={onContinue}
        style={{
          background: TEAL,
          color: '#fff',
          borderRadius: 12,
          padding: '16px 32px',
          fontFamily: F,
          fontSize: 16,
          border: 'none',
          width: '100%',
          cursor: 'pointer',
        }}
      >
        Continue
      </button>
    </div>
  );
}
